import { useQuery } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";
import type { Project } from "./useProjects";

export interface ProjectSummary {
  project: Project;
  totalBudget: number;
  totalBudgetLines: number;
  costCodeCount: number;
  divisionTotals: Record<string, number>;
  topDivisions: Array<{
    division: string;
    total: number;
    percentage: number;
  }>;
}

interface ProjectSummaryResponse {
  project: Project;
  budgetSummary: {
    totalBudget: number;
    totalLines: number;
    divisionTotals: Record<string, number>;
  };
  costCodeCount: number;
}

export function useProjectSummary(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "summary"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const response = await fetchApi<ProjectSummaryResponse>(
        `/api/projects/${projectId}/summary`,
        {
          headers,
        }
      );

      // Calculate top divisions by budget
      const divisionEntries = Object.entries(response.budgetSummary.divisionTotals);
      const totalBudget = response.budgetSummary.totalBudget;

      const topDivisions = divisionEntries
        .map(([division, total]) => ({
          division,
          total,
          percentage: totalBudget > 0 ? (total / totalBudget) * 100 : 0,
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);

      return {
        project: response.project,
        totalBudget: response.budgetSummary.totalBudget,
        totalBudgetLines: response.budgetSummary.totalLines,
        costCodeCount: response.costCodeCount,
        divisionTotals: response.budgetSummary.divisionTotals,
        topDivisions,
      } as ProjectSummary;
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
